// Agent Client - Comunicacion con agente local
import axios, { type AxiosError } from 'axios';
import type {
  PickerStatusResponse,
  PickerResult,
  PickerStartResponse,
  PickerStopResponse,
} from '../types/picker';
import type {
  RecorderStartResponse,
  RecorderStopResponse,
  RecorderEventsResponse,
  RecorderStatus,
} from '../types/recorder';
import type { AgentWorkflow } from './workflowTransformer';

export type AgentStatus = {
  status: 'connected' | 'degraded' | 'unauthorized' | 'timeout' | 'error' | 'disconnected';
  version?: string;
  engine_version?: string;
  os?: { system: string; release: string };
  python?: string;
  lastSeen?: string;
  can_execute?: boolean;
  degraded_reason?: string;
  error_code?: string;
  features?: Record<string, boolean>;
  error?: string;
  httpStatus?: number;
};

export type OverlaySettingSource = 'env' | 'config' | 'default';

export type OverlaySetting = {
  success: boolean;
  enabled: boolean;
  source?: OverlaySettingSource;
  locked?: boolean;
  error?: string;
  httpStatus?: number;
};

export type WorkflowExecutionResult = {
  status: 'running' | 'completed' | 'completed_with_errors' | 'stopped' | 'error' | 'timeout';
  progress?: number;
  currentStep?: number;
  totalSteps?: number;
  logs?: string[];
  error?: string;
  warning?: string;
  warnings?: string[];
  failedNodesCount?: number;
  errorCode?: string;
  httpStatus?: number;
};

export type AgentExecutionStatus = {
  status: 'idle' | 'running' | 'paused' | 'success' | 'completed_with_errors' | 'error' | 'stopped';
  logs?: string[];
  error?: string;
  warning?: string;
  warnings?: string[];
  failedNodesCount?: number;
  executedNodes?: number;
  totalNodes?: number;
  durationSeconds?: number;
  errorCode?: string;
  httpStatus?: number;
  completed?: boolean;
  paused?: boolean;
};

export type AgentStoredFile = {
  name: string;
  path: string;
  relativePath?: string;
  size: number;
  modified: number | string;
};

import {
  getEffectiveAgentUrl,
  getEffectiveApiKey,
  getEffectiveTimeout,
} from '../stores/settingsStore';

/**
 * H-F019: Valida si una URL apunta a una direccion de red local.
 * Retorna true para localhost, 127.0.0.1, y rangos de IP privada
 * (10.x.x.x, 172.16-31.x.x, 192.168.x.x).
 */
function isLocalUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    const hostname = parsed.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1') {
      return true;
    }
    // Rangos de IP privada
    const ipMatch = hostname.match(/^(\d+)\.(\d+)\.(\d+)\.(\d+)$/);
    if (ipMatch) {
      const [, a, b] = ipMatch.map(Number);
      if (a === 10) return true;
      if (a === 172 && b >= 16 && b <= 31) return true;
      if (a === 192 && b === 168) return true;
    }
    return false;
  } catch {
    return false;
  }
}

type AgentErrorDetails = {
  message: string;
  httpStatus?: number;
  errorCode?: string;
  backendStatus?: string;
};

function _buildAxiosClient() {
  const agentUrl = getEffectiveAgentUrl();
  // H-F019: Advertir si la URL del agente no es local
  if (agentUrl && !isLocalUrl(agentUrl)) {
    console.warn(
      `[AgentClient] La URL del agente "${agentUrl}" no apunta a una direccion de red local. ` +
      'Esto puede exponer datos sensibles. Se recomienda usar localhost o IPs privadas.'
    );
  }
  const client = axios.create({
    baseURL: agentUrl,
    timeout: getEffectiveTimeout(),
    headers: { 'Content-Type': 'application/json' },
  });
  // Interceptor que inyecta la API key fresca en cada peticion.
  // Esto elimina race conditions: la key se lee del store justo antes de enviar.
  client.interceptors.request.use((config) => {
    const apiKey = getEffectiveApiKey();
    if (apiKey) {
      config.headers.Authorization = `Bearer ${apiKey}`;
    }
    return config;
  });
  return client;
}

class AgentClient {
  private client = _buildAxiosClient();

  /** Recrea la instancia axios con los valores actuales de settings. */
  reconfigure(): void {
    this.client = _buildAxiosClient();
  }

  // Sesiones independientes de polling para no mezclar ejecuciones concurrentes.
  private _pollingSessionSequence = 0;
  private _cancelledPollingSessions = new Set<number>();

  beginPollingSession(): number {
    const sessionId = ++this._pollingSessionSequence;
    this._cancelledPollingSessions.delete(sessionId);
    // [H-FE007] Limpiar IDs obsoletos del Set para evitar memory leak
    const threshold = this._pollingSessionSequence - 10;
    for (const id of this._cancelledPollingSessions) {
      if (id < threshold) {
        this._cancelledPollingSessions.delete(id);
      }
    }
    return sessionId;
  }

  private isPollingSessionCancelled(sessionId: number | undefined): boolean {
    return typeof sessionId === 'number' && this._cancelledPollingSessions.has(sessionId);
  }

  private cancelPollingSession(sessionId: number | undefined): void {
    if (typeof sessionId === 'number') {
      this._cancelledPollingSessions.add(sessionId);
    }
  }

  private releasePollingSession(sessionId: number | undefined): void {
    if (typeof sessionId === 'number') {
      this._cancelledPollingSessions.delete(sessionId);
    }
  }

  private asRecord(value: unknown): Record<string, unknown> {
    return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
  }

  private parseOverlaySource(value: unknown): OverlaySettingSource | undefined {
    if (value === 'env' || value === 'config' || value === 'default') {
      return value;
    }
    return undefined;
  }

  private extractErrorDetails(error: unknown, fallback: string): AgentErrorDetails {
    if (axios.isAxiosError(error)) {
      const data = this.asRecord(error.response?.data);
      const payloadError = data.error;
      const payloadMessage = data.message;
      const payloadWarning = data.warning;
      const degradedReason = data.degraded_reason;

      const message =
        (typeof payloadError === 'string' && payloadError.trim()) ||
        (typeof payloadMessage === 'string' && payloadMessage.trim()) ||
        (typeof payloadWarning === 'string' && payloadWarning.trim()) ||
        (typeof degradedReason === 'string' && degradedReason.trim()) ||
        (error.message.trim() || fallback);

      return {
        message,
        httpStatus: error.response?.status,
        errorCode: typeof data.error_code === 'string' ? data.error_code : undefined,
        backendStatus: typeof data.status === 'string' ? data.status : undefined,
      };
    }

    if (error instanceof Error && error.message.trim()) {
      return { message: error.message };
    }

    return { message: fallback };
  }

  private extractPayloadErrorDetails(
    payload: unknown,
    fallback: string,
    httpStatus?: number
  ): AgentErrorDetails {
    const data = this.asRecord(payload);
    const payloadError = data.error;
    const payloadMessage = data.message;
    const payloadWarning = data.warning;
    const degradedReason = data.degraded_reason;

    return {
      message:
        (typeof payloadError === 'string' && payloadError.trim()) ||
        (typeof payloadMessage === 'string' && payloadMessage.trim()) ||
        (typeof payloadWarning === 'string' && payloadWarning.trim()) ||
        (typeof degradedReason === 'string' && degradedReason.trim()) ||
        fallback,
      httpStatus,
      errorCode: typeof data.error_code === 'string' ? data.error_code : undefined,
      backendStatus: typeof data.status === 'string' ? data.status : undefined,
    };
  }

  private isTimeoutError(error: unknown): boolean {
    if (axios.isAxiosError(error)) {
      return error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT';
    }

    return error instanceof Error && error.message.toLowerCase().includes('timeout');
  }

  private buildConnectionErrorStatus(error: unknown, fallback: string): AgentStatus {
    const details = this.extractErrorDetails(error, fallback);
    const message = details.message;

    if (axios.isAxiosError(error)) {
      const httpStatus = details.httpStatus;

      if (httpStatus === 401) {
        return {
          status: 'unauthorized',
          error: message,
          httpStatus,
          error_code: details.errorCode,
        };
      }

      if (this.isTimeoutError(error)) {
        return {
          status: 'timeout',
          error: message,
          httpStatus,
          error_code: details.errorCode,
        };
      }

      if (httpStatus !== undefined) {
        return {
          status: 'error',
          error: message,
          httpStatus,
          error_code: details.errorCode,
        };
      }

      return {
        status: 'disconnected',
        error: message,
        error_code: details.errorCode,
      };
    }

    if (this.isTimeoutError(error)) {
      return {
        status: 'timeout',
        error: message,
        error_code: details.errorCode,
      };
    }

    return {
      status: 'disconnected',
      error: message,
      error_code: details.errorCode,
    };
  }

  // Verificar si agente está conectado
  async checkStatus(): Promise<AgentStatus> {
    let healthData: Record<string, unknown>;

    try {
      const response = await this.client.get('/health');
      healthData = this.asRecord(response.data);
    } catch (error) {
      return this.buildConnectionErrorStatus(error, 'No se pudo conectar con el agente');
    }

    const reportedHealthStatus = healthData.status;
    const canExecute = healthData.can_execute;
    // H-F011: Extraer explicitamente solo campos conocidos en vez de spread inseguro
    const baseStatus: AgentStatus = {
      status:
        reportedHealthStatus === 'degraded' ||
        reportedHealthStatus === 'error'
          ? reportedHealthStatus
          : canExecute === false
            ? 'error'
            : 'connected',
      version: typeof healthData.version === 'string' ? healthData.version : undefined,
      engine_version: typeof healthData.engine_version === 'string' ? healthData.engine_version : undefined,
      os: typeof healthData.os === 'object' && healthData.os !== null
        ? healthData.os as AgentStatus['os']
        : undefined,
      python: typeof healthData.python === 'string' ? healthData.python : undefined,
      features: typeof healthData.features === 'object' && healthData.features !== null
        ? healthData.features as Record<string, boolean>
        : undefined,
      can_execute: typeof canExecute === 'boolean' ? canExecute : undefined,
      degraded_reason: typeof healthData.degraded_reason === 'string' ? healthData.degraded_reason : undefined,
    };

    if (reportedHealthStatus === 'error' || canExecute === false) {
      return {
        ...baseStatus,
        status: 'error',
        error:
          typeof healthData.error === 'string'
            ? healthData.error
            : typeof healthData.degraded_reason === 'string'
              ? healthData.degraded_reason
              : 'El agente reportó que no puede ejecutar workflows',
      };
    }

    // [H-FE002/H-FE003] Eliminada segunda llamada a GET /execute/status.
    // La autenticación se valida implícitamente con /health y cuando el frontend ejecuta un workflow.
    // La doble llamada causaba falsos errores: si /execute/status fallaba, el agente se reportaba
    // como error aunque /health respondió OK. Además, el spread mezclaba propiedades de ambas respuestas.
    return baseStatus;
  }

  /**
   * Verifica si el agente es una version legacy que requiere API Key.
   * Llama a un endpoint protegido sin API Key.
   * Si retorna 401, el agente es legacy y no soporta auto-conexion.
   * Solo se llama UNA VEZ en la primera conexion exitosa.
   */
  async probeAuthRequired(): Promise<boolean> {
    // Solo probar en localhost. Desde remoto, siempre da 401 (API Key requerida)
    // y eso no significa que sea legacy — simplemente no es localhost.
    const agentUrl = getEffectiveAgentUrl();
    try {
      const parsed = new URL(agentUrl);
      const host = parsed.hostname;
      if (host !== 'localhost' && host !== '127.0.0.1' && host !== '::1') {
        return false; // Remoto: no es legacy, solo necesita API Key
      }
    } catch {
      // URL invalida, continuar con el probe
    }

    try {
      // Crear cliente temporal SIN API Key para probar
      const tempClient = axios.create({
        baseURL: agentUrl,
        timeout: 5000,
        headers: { 'Content-Type': 'application/json' },
        // Sin interceptor de API Key — probar sin auth
      });
      await tempClient.get('/execute/status');
      return false; // 200 = no requiere auth = agente nuevo
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        return true; // 401 = requiere auth = agente legacy
      }
      return false; // Otro error = no es problema de auth
    }
  }

  // Ejecutar workflow
  async executeWorkflow(
    workflow: AgentWorkflow,
    onProgress?: (logs: string[]) => void,
    pollingSessionId?: number
  ): Promise<WorkflowExecutionResult> {
    const activeSessionId = pollingSessionId ?? this.beginPollingSession();
    try {
      const response = await this.client.post('/execute', workflow);
      const data = response.data as Record<string, unknown>;

      // El agente siempre retorna 202 (started) y ejecuta en background.
      // No existe respuesta sincrona — si no es 202/started, es un error.
      if (response.status === 202 || data.status === 'started') {
        return await this.pollExecutionStatus(onProgress, activeSessionId);
      }

      // Respuesta inesperada del agente
      const details = this.extractPayloadErrorDetails(
        data,
        `Respuesta inesperada del agente (HTTP ${response.status}). Se esperaba 202.`,
        response.status
      );
      return {
        status: 'error',
        error: details.message,
        errorCode: details.errorCode,
        httpStatus: details.httpStatus,
      };
    } catch (error) {
      const details = this.extractErrorDetails(error, 'Error al ejecutar workflow');
      return {
        status: 'error',
        error: details.message,
        errorCode: details.errorCode,
        httpStatus: details.httpStatus,
      };
    }
  }

  /**
   * Polling del estado de ejecucion hasta que termine.
   * Consulta GET /execute/status cada segundo.
   * Llama a onProgress con los logs acumulados en cada poll.
   *
   * [H-T2-004] Hecho publico para permitir reanudacion de polling al recargar pagina.
   * [H-T2-005] Retorna status 'timeout' si excede 10 min, para que el hook pregunte al usuario.
   */
  async pollExecutionStatus(
    onProgress?: (logs: string[]) => void,
    pollingSessionId?: number
  ): Promise<WorkflowExecutionResult> {
    const activeSessionId = pollingSessionId ?? this.beginPollingSession();
    const ownsSession = pollingSessionId === undefined;
    const maxAttempts = 600; // 10 minutos maximo
    const pollInterval = 1000; // 1 segundo
    const MAX_CONSECUTIVE_FAILURES = 5;
    let consecutiveFailures = 0;
    // Trackear ultimo status para detectar reinicio del agente (running -> idle)
    let lastStatus: string | undefined;
    // Contar polls consecutivos en 'idle' para detectar ejecucion que nunca arranco
    let idlePolls = 0;
    const MAX_IDLE_POLLS = 10;
    let lastFailure: AgentErrorDetails | null = null;

    try {
      for (let i = 0; i < maxAttempts; i++) {
        if (this.isPollingSessionCancelled(activeSessionId)) {
          return { status: 'stopped', logs: [] };
        }

        await new Promise(resolve => setTimeout(resolve, pollInterval));

        try {
          const response = await this.client.get('/execute/status');
          const data = response.data as Record<string, unknown>;
          const status = data.status as string | undefined;
          consecutiveFailures = 0;

          // Detectar reinicio del agente: si estaba 'running' y ahora es 'idle',
          // el agente se reinicio y perdio la ejecucion en curso
          if (lastStatus === 'running' && status === 'idle') {
            return {
              status: 'error',
              error: 'El agente se reinicio durante la ejecucion. El workflow no se completo.',
            };
          }

          // Contar polls consecutivos en 'idle' — si la ejecucion nunca arranco
          if (status === 'idle') {
            idlePolls++;
            if (idlePolls >= MAX_IDLE_POLLS) {
              return {
                status: 'error',
                error: 'La ejecucion no inicio en el agente. Verifique que el agente este funcionando correctamente.',
              };
            }
          } else {
            idlePolls = 0;
          }

          lastStatus = status;

          if (onProgress && Array.isArray(data.logs)) {
            // H-F016: Validar que cada elemento sea string antes del cast
            onProgress(data.logs.filter((l): l is string => typeof l === 'string'));
          }

          // 'paused' no es terminal — seguir polling para detectar cuando se reanude o detenga
          if (status && status !== 'running' && status !== 'idle' && status !== 'paused') {
            return this.parseExecutionResult(data);
          }
        } catch (error) {
          consecutiveFailures++;
          lastFailure = this.extractErrorDetails(
            error,
            'El agente dejó de responder durante la ejecución'
          );
          if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
            // Detener la ejecucion en el agente para evitar que siga corriendo sin supervision
            try {
              await this.client.post('/execute/stop');
            } catch {
              // Si la red esta caida, el stop tambien falla — no hay nada mas que hacer
            }
            return {
              status: 'error',
              error: lastFailure.message,
              errorCode: lastFailure.errorCode,
              httpStatus: lastFailure.httpStatus,
            };
          }
        }
      }

      return {
        status: 'timeout',
        error: 'La ejecucion lleva mas de 10 minutos y sigue en curso.',
      };
    } finally {
      if (ownsSession || this.isPollingSessionCancelled(activeSessionId)) {
        this.releasePollingSession(activeSessionId);
      }
    }
  }

  /**
   * Parsea la respuesta del agente al formato del frontend.
   */
  private parseExecutionResult(data: Record<string, unknown>): WorkflowExecutionResult {
    let mappedStatus: WorkflowExecutionResult['status'];
    if (
      data.status === 'completed_with_errors' ||
      (data.status === 'success' && data.had_errors === true)
    ) {
      mappedStatus = 'completed_with_errors';
    } else if (data.status === 'success') {
      mappedStatus = 'completed';
    }
    else if (data.status === 'stopped') mappedStatus = 'stopped';
    else mappedStatus = 'error';

    const executedNodes = typeof data.executed_nodes === 'number' ? data.executed_nodes : undefined;
    const totalNodes = typeof data.total_nodes === 'number' ? data.total_nodes : undefined;

    const result: WorkflowExecutionResult = {
      status: mappedStatus,
      // [H-FE006] Validar que data.logs sea un array antes de hacer cast
      logs: Array.isArray(data.logs) ? (data.logs as string[]) : [],
      error: data.error as string | undefined,
      warning: typeof data.warning === 'string' ? data.warning : undefined,
      warnings: Array.isArray(data.warnings) ? (data.warnings as string[]) : undefined,
      failedNodesCount: typeof data.failed_nodes_count === 'number'
        ? data.failed_nodes_count
        : undefined,
      errorCode: typeof data.error_code === 'string' ? data.error_code : undefined,
    };

    if (executedNodes !== undefined) {
      result.currentStep = executedNodes;
      result.totalSteps = totalNodes ?? executedNodes;
    }

    if (executedNodes !== undefined && totalNodes && totalNodes > 0) {
      result.progress = Math.min(100, Math.round((executedNodes / totalNodes) * 100));
    } else if (data.duration_seconds !== undefined) {
      result.progress =
        result.status === 'completed' || result.status === 'completed_with_errors'
          ? 100
          : 0;
    }

    return result;
  }

  private parseCurrentExecutionStatus(data: Record<string, unknown>): AgentExecutionStatus {
    const rawStatus = data.status;
    const status: AgentExecutionStatus['status'] =
      rawStatus === 'idle' ||
      rawStatus === 'running' ||
      rawStatus === 'paused' ||
      rawStatus === 'success' ||
      rawStatus === 'completed_with_errors' ||
      rawStatus === 'error' ||
      rawStatus === 'stopped'
        ? rawStatus
        : 'error';

    return {
      status,
      logs: Array.isArray(data.logs) ? (data.logs as string[]) : [],
      error: typeof data.error === 'string' ? data.error : undefined,
      warning: typeof data.warning === 'string' ? data.warning : undefined,
      warnings: Array.isArray(data.warnings) ? (data.warnings as string[]) : undefined,
      failedNodesCount: typeof data.failed_nodes_count === 'number'
        ? data.failed_nodes_count
        : undefined,
      executedNodes: typeof data.executed_nodes === 'number' ? data.executed_nodes : undefined,
      totalNodes: typeof data.total_nodes === 'number' ? data.total_nodes : undefined,
      durationSeconds: typeof data.duration_seconds === 'number' ? data.duration_seconds : undefined,
      errorCode: typeof data.error_code === 'string' ? data.error_code : undefined,
      completed: data.completed === true,
    };
  }

  // Detener ejecución del workflow
  async stopExecution(pollingSessionId?: number): Promise<{ status: 'success'; message: string }> {
    try {
      const response = await this.client.post('/execute/stop');
      const result = response.data as Record<string, unknown>;

      if (result.status !== 'success') {
        throw new Error(
          typeof result.error === 'string'
            ? result.error
            : typeof result.message === 'string'
              ? result.message
              : 'El agente no confirmó la detención de la ejecución'
        );
      }

      this.cancelPollingSession(pollingSessionId);
      // [H-FE007] Liberar la sesión cancelada para evitar memory leak en el Set
      this.releasePollingSession(pollingSessionId);
      return {
        status: 'success',
        message: typeof result.message === 'string' ? result.message : 'Ejecución detenida',
      };
    } catch (error) {
      const details = this.extractErrorDetails(error, 'Error al detener ejecución');
      throw new Error(details.message);
    }
  }

  // Pausar ejecución del workflow (entre nodos)
  async pauseExecution(): Promise<void> {
    try {
      await this.client.post('/execute/pause');
    } catch (error) {
      const details = this.extractErrorDetails(error, 'Error al pausar ejecucion');
      throw new Error(details.message);
    }
  }

  // Reanudar ejecución del workflow despues de pausa
  async resumeExecution(): Promise<void> {
    try {
      await this.client.post('/execute/resume');
    } catch (error) {
      const details = this.extractErrorDetails(error, 'Error al reanudar ejecucion');
      throw new Error(details.message);
    }
  }

  // ==================== ELEMENT PICKER ====================

  /**
   * Inicia el selector de elementos.
   * El usuario podrá mover el mouse sobre la aplicación y ver
   * elementos resaltados en rojo. CTRL+Click captura el elemento.
   */
  /**
   * Verifica si un error HTTP es 404 o 501 (endpoint no soportado).
   */
  private isEndpointNotSupported(error: unknown): boolean {
    if (axios.isAxiosError(error)) {
      const axiosErr = error as AxiosError;
      return axiosErr.response?.status === 404 || axiosErr.response?.status === 501;
    }
    return false;
  }

  async startElementPicker(mode: 'desktop' | 'web' = 'desktop', delay: number = 5): Promise<PickerStartResponse> {
    // Validar delay: entero entre 1 y 30, fallback a 5 si valor invalido
    const safeDelay = Math.max(1, Math.min(Math.floor(delay) || 5, 30));
    try {
      const response = await this.client.post('/picker/start', { mode, delay: safeDelay });
      return response.data;
    } catch (error) {
      if (this.isEndpointNotSupported(error)) {
        return { status: 'error', error: 'El picker no esta soportado en esta version del agente' };
      }
      const details = this.extractErrorDetails(error, 'Error al iniciar picker');
      return { status: 'error', error: details.message };
    }
  }

  /**
   * Obtiene el estado actual del picker.
   * Usar para polling mientras el usuario selecciona elemento.
   */
  async getPickerStatus(): Promise<PickerStatusResponse> {
    try {
      const response = await this.client.get('/picker/status');
      return response.data;
    } catch (error) {
      if (this.isEndpointNotSupported(error)) {
        return { status: 'error', error: 'El picker no esta soportado en esta version del agente' };
      }
      const details = this.extractErrorDetails(error, 'Error obteniendo estado del picker');
      return { status: 'error', error: details.message };
    }
  }

  /**
   * Obtiene el resultado de la captura (elemento seleccionado).
   * Solo llamar cuando getPickerStatus retorna status='captured'.
   * [H-F-012] Timeout especifico de 15s (menor que el default de 30s)
   * porque el resultado ya debería estar disponible en el agente.
   */
  async getPickerResult(): Promise<PickerResult> {
    try {
      const response = await this.client.get('/picker/result', { timeout: 15000 });
      return response.data;
    } catch (error) {
      if (this.isEndpointNotSupported(error)) {
        return { status: 'error', error: 'El picker no esta soportado en esta version del agente' };
      }
      const details = this.extractErrorDetails(error, 'Error obteniendo resultado');
      return { status: 'error', error: details.message };
    }
  }

  /**
   * Detiene el picker y limpia recursos.
   * Llamar siempre al cerrar el modal o cancelar.
   */
  async stopPicker(): Promise<PickerStopResponse> {
    try {
      const response = await this.client.post('/picker/stop');
      const result = response.data as PickerStopResponse;

      if (result.status !== 'stopped') {
        throw new Error(result.error || 'El agente no confirmó la detención del picker');
      }

      return result;
    } catch (error) {
      if (this.isEndpointNotSupported(error)) {
        throw new Error('El picker no esta soportado en esta version del agente');
      }
      throw new Error(this.extractErrorDetails(error, 'Error deteniendo picker').message);
    }
  }

  // Obtener lista de aplicaciones instaladas en el agente
  async getInstalledApps(): Promise<{ name: string; path: string }[]> {
    try {
      const response = await this.client.get('/desktop/applications');
      const data = response.data as Record<string, unknown>;
      return Array.isArray(data.applications)
        ? (data.applications as { name: string; path: string }[])
        : [];
    } catch (error) {
      // No loguear como error — el endpoint puede no existir en agentes antiguos
      if (!this.isEndpointNotSupported(error)) {
        console.warn('Error obteniendo aplicaciones instaladas:', error);
      }
      return [];
    }
  }

  // Obtener logs en tiempo real
  async getLogs(lines: number = 100): Promise<string[]> {
    try {
      const response = await this.client.get('/logs', { params: { lines } });
      return response.data.logs || [];
    } catch (error) {
      // [H-FE005] Loggear error en vez de tragarlo silenciosamente
      console.warn('Error obteniendo logs:', error);
      return [];
    }
  }

  // Ejecutar diagnóstico del sistema
  async runDiagnostic(): Promise<Record<string, unknown>> {
    try {
      const response = await this.client.get('/diagnostic');
      return response.data as Record<string, unknown>;
    } catch (error) {
      throw new Error(this.extractErrorDetails(error, 'No se pudo conectar con el agente').message);
    }
  }

  // Guardar archivo localmente en el agente
  async saveFile(
    filename: string,
    content: string,
    fileType: 'csv' | 'xlsx' | 'xls' = 'csv'
  ): Promise<{ success: boolean; filePath?: string; relativePath?: string; headers?: string[]; totalRows?: number; error?: string }> {
    try {
      const response = await this.client.post('/files/save', {
        filename,
        content,
        fileType,
      });
      return response.data;
    } catch (error) {
      const details = this.extractErrorDetails(error, 'Error al guardar archivo');
      return {
        success: false,
        error: details.message,
      };
    }
  }

  // Listar archivos guardados localmente en el agente
  async listFiles(): Promise<{ success: boolean; files: AgentStoredFile[]; error?: string }> {
    try {
      const response = await this.client.get('/files/list');
      const files = Array.isArray(response.data.files)
        ? (response.data.files as AgentStoredFile[])
        : [];

      return {
        success: true,
        files,
      };
    } catch (error) {
      const details = this.extractErrorDetails(error, 'Error al listar archivos');
      return {
        success: false,
        files: [],
        error: details.message,
      };
    }
  }

  // Eliminar archivo localmente en el agente
  async deleteFile(filePath: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await this.client.post('/files/delete', {
        filePath,
      });
      return response.data;
    } catch (error) {
      const details = this.extractErrorDetails(error, 'Error al eliminar archivo');
      return {
        success: false,
        error: details.message,
      };
    }
  }

  // Validar ruta local de archivo Excel/CSV en la máquina del agente
  async validateLocalFile(localPath: string): Promise<{
    success: boolean;
    name?: string;
    headers?: string[];
    totalRows?: number;
    error?: string;
  }> {
    try {
      const response = await this.client.post('/files/validate-local', { localPath });
      return response.data;
    } catch (error) {
      const details = this.extractErrorDetails(error, 'Error al validar archivo local');
      return { success: false, error: details.message };
    }
  }

  async getOverlaySetting(): Promise<OverlaySetting> {
    try {
      const response = await this.client.get('/settings/overlay');
      const data = this.asRecord(response.data);
      return {
        success: data.success === true,
        enabled: data.enabled === true,
        source: this.parseOverlaySource(data.source),
        locked: data.locked === true,
      };
    } catch (error) {
      const details = this.extractErrorDetails(error, 'No se pudo obtener la configuracion del overlay');
      return {
        success: false,
        enabled: false,
        error: details.message,
        httpStatus: details.httpStatus,
      };
    }
  }

  async setOverlaySetting(enabled: boolean): Promise<OverlaySetting> {
    try {
      const response = await this.client.post('/settings/overlay', { enabled });
      const data = this.asRecord(response.data);
      return {
        success: data.success === true,
        enabled: data.enabled === true,
        source: this.parseOverlaySource(data.source),
        locked: data.locked === true,
        error: typeof data.error === 'string' ? data.error : undefined,
      };
    } catch (error) {
      const details = this.extractErrorDetails(error, 'No se pudo actualizar la configuracion del overlay');
      return {
        success: false,
        enabled,
        error: details.message,
        httpStatus: details.httpStatus,
      };
    }
  }

  // ==================== RECORDER ====================

  /** Inicia la grabación de secuencias en el agente. */
  async startRecording(): Promise<RecorderStartResponse> {
    try {
      const response = await this.client.post('/recorder/start');
      return response.data as RecorderStartResponse;
    } catch (error) {
      const details = this.extractErrorDetails(error, 'No se pudo iniciar la grabación');
      return { status: 'error', error: details.message };
    }
  }

  /** Detiene la grabación y retorna todos los eventos capturados. */
  async stopRecording(): Promise<RecorderStopResponse> {
    try {
      const response = await this.client.post('/recorder/stop');
      return response.data as RecorderStopResponse;
    } catch (error) {
      const details = this.extractErrorDetails(error, 'No se pudo detener la grabación');
      return { status: 'error', error: details.message };
    }
  }

  /** Obtiene eventos nuevos desde el último poll (cursor-based). */
  async getRecorderEvents(afterId?: string): Promise<RecorderEventsResponse> {
    try {
      const params = afterId ? { after: afterId } : {};
      const response = await this.client.get('/recorder/events', { params });
      return response.data as RecorderEventsResponse;
    } catch (error) {
      const details = this.extractErrorDetails(error, 'Error obteniendo eventos del grabador');
      return { status: 'error', error: details.message };
    }
  }

  /** Obtiene el estado actual del grabador. */
  async getRecorderStatus(): Promise<RecorderStatus> {
    try {
      const response = await this.client.get('/recorder/status');
      return response.data as RecorderStatus;
    } catch {
      return { status: 'idle', eventCount: 0 };
    }
  }

  // Detener el agente de forma segura
  async shutdownAgent(): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const response = await this.client.post('/shutdown');
      return response.data as { success: boolean; message?: string };
    } catch (error) {
      const details = this.extractErrorDetails(error, 'Error al detener el agente');
      return { success: false, error: details.message };
    }
  }

  // Obtener estado de ejecución actual
  async getExecutionStatus(): Promise<AgentExecutionStatus> {
    try {
      const response = await this.client.get('/execute/status');
      return this.parseCurrentExecutionStatus(response.data as Record<string, unknown>);
    } catch (error) {
      const details = this.extractErrorDetails(error, 'Error obteniendo estado de ejecución');
      return {
        status: 'error',
        error: details.message,
        errorCode: details.errorCode,
        httpStatus: details.httpStatus,
        logs: [],
      };
    }
  }
}

export const agentClient = new AgentClient();
